import CopyButton from "@components/buttons/CopyButton";
import { GetIvyInstanceData } from "@services/models/Prime/GetIvyInstancesModel";
import { ColDef } from "ag-grid-community";
import ConstantFunctions from "src/utils/constants_functions";
import { getEuropeTerminalSets } from "../utils/primeOrderRowClass";
import {
  ActiveDurationPill,
  ActiveDurationTone,
  getEstSettlementTimePill,
  getEuropeEventPillClass,
  getEuropeStatusDisplayText,
  getSettlementTypePill,
  getTransferTypePill,
  readOrderField,
  TablePill,
} from "./PrimeOrderPills";

const constantFunctions = new ConstantFunctions();

const sortEuropeEventsAsc = (
  a: { eventId?: string | number | null; timestamp?: string | null },
  b: { eventId?: string | number | null; timestamp?: string | null },
) => {
  const timeDiff =
    new Date(a.timestamp || Date.now()).getTime() -
    new Date(b.timestamp || Date.now()).getTime();

  if (timeDiff !== 0) {
    return timeDiff;
  }

  return Number(a.eventId ?? 0) - Number(b.eventId ?? 0);
};

// Europe Event IDs mapping
export const EUROPE_EVENT_IDS = {
  RECEIVED: "1",
  CREATED: "2",
  ORDER_PROCESSING: "3",
  PAYMENT_PROCESSING: "4",
  COMPLETED: "5",
  REFUNDED: "7",
  FAILED: "8",
  CANCELLED: "9",
  KYC_FAILED: "10",
  EXTERNAL_COMPLETED: "13",
};

// Europe Amount Cell Renderer
export const EuropeAmountCellRenderer = (params: any) => {
  const data = params.data as GetIvyInstanceData;
  return (
    <div className="flex items-center text-[10px] text-text-base font-medium text-gray-700">
      <div className="flex items-center w-[50px]">
        <img
          src={data.cryptoCurrencyImage}
          alt=""
          className="h-[12px] w-[12px] mr-1"
        />
        <span>{data.cryptoAmount?.toFixed(2) ?? "- -"}</span>
      </div>
      <span className="mx-1">|</span>
      <div className="flex items-center ml-[4px]">
        <img
          src={data.fiatCurrencyImage}
          alt=""
          className="h-[12px] w-[12px] mr-1"
        />
        <span>{data.fiatAmount?.toFixed(2) ?? "- -"}</span>
      </div>
    </div>
  );
};

const getEuropeEventTimeDiffMs = (
  data: GetIvyInstanceData,
  eventId: string,
): number => {
  if (!data.eventDetails || data.eventDetails.length === 0) {
    return 0;
  }

  const sortedEvents = [...data.eventDetails].sort(sortEuropeEventsAsc);

  const eventIndex = sortedEvents.findIndex(
    (e) => e.eventId?.toString() === eventId,
  );
  if (eventIndex === -1) {
    return 0;
  }

  const currentEventTime = new Date(
    sortedEvents[eventIndex].timestamp || Date.now(),
  ).getTime();
  const prevEventTime =
    eventIndex === 0
      ? Date.now()
      : new Date(
          sortedEvents[eventIndex - 1].timestamp || Date.now(),
        ).getTime();

  return Math.abs(currentEventTime - prevEventTime);
};

// Europe Active Cell Renderer
export const EuropeActiveCellRenderer = (params: any) => {
  const data = params.data as GetIvyInstanceData;

  const durationFromMs = (diffMs: number, tone: ActiveDurationTone) => (
    <ActiveDurationPill diffMs={diffMs} tone={tone} />
  );

  const sortedEventDetailsFromApi = [...(data.eventDetails || [])].sort(
    (a, b) => sortEuropeEventsAsc(b, a),
  );

  if (data.eventDetails && data.eventDetails.length > 0) {
    const latestEvent = sortedEventDetailsFromApi[0];
    const latestEventId = latestEvent.eventId?.toString() ?? "";
    const { completed, expired } = getEuropeTerminalSets(data.transferType);
    const isFailed = expired.has(latestEventId);
    const isCompleted = completed.has(latestEventId);

    if (isFailed || isCompleted) {
      return durationFromMs(
        new Date(latestEvent.timestamp || data.createdAt).getTime() -
          new Date(data.createdAt).getTime(),
        isFailed ? "failed" : "completed",
      );
    }
  }

  return durationFromMs(
    Date.now() - new Date(data.createdAt).getTime(),
    "active",
  );
};

// Europe Column Definitions
export const createEuropeOrderColumnDefs = (): ColDef[] => [
  {
    headerName: "Date",
    field: "createdAt",
    flex: 0.9,
    minWidth: 90,
    maxWidth: 130,
    cellRenderer: (params: any) => {
      return (
        <span className="text-[10px] text-text-base font-medium">
          {constantFunctions.dateFormatInDDMMYY(params.data.createdAt)}
        </span>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agDateColumnFilter",
  },
  {
    headerName: "Instance ID",
    field: "instanceId",
    flex: 1.1,
    minWidth: 150,
    maxWidth: 160,
    cellRenderer: (params: any) => {
      const { data } = params;
      return (
        <div className="flex items-start h-full gap-1">
          <span
            className={`text-[10px] font-medium px-[6px] h-[24px] rounded flex items-center mr-1 ${
              data.isBuying === 1 ? "bg-bg-status-green" : "bg-bg-status-red"
            } text-text-base`}
          >
            {data.isBuying === 1 ? "B" : "S"}
          </span>

          <div className="flex flex-col leading-[10px] flex-1">
            {data.instanceId ? (
              Array.from(
                {
                  length: Math.min(
                    Math.ceil(data.instanceId.toString().length / 12),
                    3,
                  ),
                },
                (_, index) => (
                  <span
                    key={index}
                    className="text-[10px] text-text-base font-medium"
                  >
                    {data.instanceId
                      .toString()
                      .slice(index * 12, (index + 1) * 12)}
                  </span>
                ),
              )
            ) : (
              <span className="text-[10px] text-text-base font-medium">
                - -
              </span>
            )}
          </div>

          {data.instanceId && (
            <CopyButton
              copyText={data.instanceId.toString()}
              copyMessage="Instance ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          )}
        </div>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Merchant",
    field: "merchantName",
    flex: 1.2,
    minWidth: 120,
    maxWidth: 180,
    cellRenderer: (params: any) => {
      return (
        <span className="text-[10px] text-text-base font-medium">
          {params.data.merchantName || "- -"}
        </span>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.2",
    },
  },
  {
    headerName: "Merchant Ref ID",
    field: "merchantRefId",
    flex: 1.2,
    minWidth: 150,
    maxWidth: 180,
    cellRenderer: (params: any) => {
      const merchantRefId = params.data.merchantRefId;
      return (
        <div className="flex items-center justify-between h-full gap-1">
          <div className="flex flex-col leading-[10px] flex-1">
            {merchantRefId ? (
              Array.from(
                {
                  length: Math.min(
                    Math.ceil(merchantRefId.toString().length / 12),
                    3,
                  ),
                },
                (_, index) => (
                  <span
                    key={index}
                    className="text-[10px] text-text-base font-medium"
                  >
                    {merchantRefId
                      .toString()
                      .slice(index * 12, (index + 1) * 12)}
                  </span>
                ),
              )
            ) : (
              <span className="text-[10px] text-text-base font-medium">
                - -
              </span>
            )}
          </div>

          {merchantRefId && (
            <CopyButton
              copyText={merchantRefId.toString()}
              copyMessage="Merchant Ref ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          )}
        </div>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Full Name",
    colId: "fullName",
    flex: 1.2,
    minWidth: 120,
    maxWidth: 180,
    valueGetter: (params) => {
      const userDetails = params.data?.userDetails;
      return [userDetails?.firstName, userDetails?.lastName]
        .filter(Boolean)
        .join(" ");
    },
    cellRenderer: (params: any) => {
      return (
        <span className="text-[10px] text-text-base font-medium">
          {params.value || "- -"}
        </span>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.2",
    },
  },
  {
    headerName: "Email",
    field: "userDetails.email",
    flex: 1.6,
    minWidth: 180,
    maxWidth: 260,
    cellRenderer: (params: any) => {
      const email = params.data?.userDetails?.email;
      return (
        <div className="flex items-center justify-between h-full gap-1">
          <span className="text-[10px] text-text-base font-medium break-all">
            {email || "- -"}
          </span>
          {email && (
            <CopyButton
              copyText={email}
              copyMessage="Email copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          )}
        </div>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.2",
    },
  },
  {
    headerName: "Amount",
    field: "amount",
    flex: 1.8,
    minWidth: 180,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: EuropeAmountCellRenderer,
  },
  {
    headerName: "Transfer Type",
    field: "transferType",
    flex: 1,
    minWidth: 110,
    maxWidth: 140,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    cellRenderer: (params: any) => {
      const pill = getTransferTypePill(
        readOrderField(params.data, "transferType", "transfer_type"),
      );
      return <TablePill text={pill.text} className={pill.className} />;
    },
  },
  {
    headerName: "Settlement Type",
    field: "settlementType",
    flex: 1,
    minWidth: 110,
    maxWidth: 140,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    cellRenderer: (params: any) => {
      const pill = getSettlementTypePill(
        readOrderField(params.data, "settlementType", "settlement_type"),
      );
      return <TablePill text={pill.text} className={pill.className} />;
    },
  },
  {
    headerName: "Est. Settlement Time",
    field: "settlementTime",
    flex: 1.2,
    minWidth: 130,
    maxWidth: 170,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    cellRenderer: (params: any) => {
      const pill = getEstSettlementTimePill(
        params.data?.settlementTime,
        params.data?.settlementType,
      );
      return <TablePill text={pill.text} className={pill.className} />;
    },
  },
  {
    headerName: "Status",
    field: "eventDescription",
    flex: 1.4,
    minWidth: 140,
    maxWidth: 220,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.2",
    },
    cellRenderer: (params: any) => {
      const data = params.data as GetIvyInstanceData;
      return (
        <TablePill
          text={getEuropeStatusDisplayText(data.eventDescription)}
          className={getEuropeEventPillClass(data.eventId, data.transferType)}
          compact
        />
      );
    },
  },
  {
    headerName: "Active",
    field: "active",
    flex: 0.8,
    minWidth: 80,
    maxWidth: 110,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: EuropeActiveCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
];
